/**
 * Subscription Modal
 * Replaces alerts for better UX - no popup blockers
 */
import React from 'react';
import { useTheme } from '../ThemeContext';
import { startingTrialMessage } from '../shared/trialCopy';
import { checkoutPlanLabel } from '../shared/planNames';
import { isNativeApp, NATIVE_PLAN_NOTICE } from '../utils/platform';
import { openExternalUrl } from '../utils/openExternal';

const SubscriptionModal = ({ isOpen, onClose, onConfirm, type, plan, agents, error }) => {
  const { colors } = useTheme();
  const brandColor = '#59c4c4';

  if (!isOpen) return null;

  const getContent = () => {
    if (isNativeApp() && (
      type === 'choose-plan'
      || type === 'starting-trial'
      || type === 'starting-checkout'
      || type === 'checkout-canceled'
      || (type === 'subscription-error' && error === NATIVE_PLAN_NOTICE)
    )) {
      return {
        title: 'Your plan',
        message: NATIVE_PLAN_NOTICE,
        actions: [],
      };
    }
    switch (type) {
      case 'choose-plan':
        return {
          title: 'Complete Your Subscription',
          message: 'Welcome! To access your dashboard, please choose your plan:',
          actions: [
            {
              label: 'Solo Agent - $49/month',
              sublabel: '200 homeowners • Perfect for individual agents',
              onClick: () => onConfirm('solo'),
              primary: false
            },
            {
              label: 'Team Plan - $229/month',
              sublabel: '500 homeowners • Best for teams',
              onClick: () => onConfirm('team'),
              primary: true
            }
          ]
        };

      case 'starting-trial':
        return {
          title: 'Starting Your Free Trial',
          message: startingTrialMessage(checkoutPlanLabel(plan, agents)),
          showSpinner: true,
          actions: []
        };

      case 'starting-checkout':
        return {
          title: 'Starting checkout',
          message: `You've selected the ${checkoutPlanLabel(plan, agents)} plan.\n\nYou'll be billed when checkout completes.\n\nRedirecting to secure checkout...`,
          showSpinner: true,
          actions: []
        };

      case 'loading-subscription':
        return {
          title: 'Setting Up Your Account...',
          message: 'Please wait while we check your subscription.\n\nThis usually takes just a few seconds.',
          showSpinner: true,
          actions: []
        };

      case 'account-ready':
        return {
          title: 'Account created',
          message: error || 'Your account was created. Sign in to finish checkout.',
          actions: []
        };

      case 'subscription-error':
        return {
          title: 'Unable to Access Dashboard',
          message: error || 'There was a problem setting up your subscription. Please contact support for assistance.',
          actions: [
            {
              label: 'Contact Support',
              onClick: () => openExternalUrl('mailto:support@pacarion.com?subject=Subscription%20Issue'),
              primary: true
            },
            {
              label: 'Try Again',
              onClick: onConfirm,
              primary: false
            }
          ]
        };

      case 'checkout-canceled':
        return {
          title: 'Checkout Canceled',
          message: 'You canceled the checkout process. To access your dashboard, you need to complete your subscription setup.\n\nsupport@pacarion.com',
          actions: [
            {
              label: 'Resume Checkout',
              onClick: onConfirm,
              primary: true
            },
            {
              label: 'Contact Support',
              onClick: () => openExternalUrl('mailto:support@pacarion.com?subject=Need%20Help%20with%20Checkout'),
              primary: false
            }
          ]
        };

      default:
        return {
          title: 'Notification',
          message: '',
          actions: []
        };
    }
  };

  const content = getContent();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem'
    }}>
      <div style={{
        background: colors.surface,
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: 'min(500px, 100%)',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        border: `1px solid ${colors.border}`
      }}>
        {/* Title */}
        <h2 style={{
          margin: '0 0 1rem 0',
          fontSize: '1.5rem',
          fontWeight: 500,
          color: colors.text,
          textAlign: 'center'
        }}>
          {content.title}
        </h2>

        {/* Message */}
        <p style={{
          margin: '0 0 2rem 0',
          fontSize: '1rem',
          color: colors.textSecondary,
          whiteSpace: 'pre-line',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          minWidth: 0,
          maxWidth: '100%',
          lineHeight: '1.6',
          textAlign: 'center'
        }}>
          {content.message}
        </p>

        {/* Spinner */}
        {content.showSpinner && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '1.5rem 0'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `4px solid ${colors.borderLight}`,
              borderTop: `4px solid ${brandColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}

        {/* Actions */}
        {content.actions.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            {content.actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                style={{
                  padding: '1rem',
                  borderRadius: '12px',
                  border: action.primary ? 'none' : `2px solid ${colors.border}`,
                  background: action.primary ? brandColor : colors.background,
                  color: action.primary ? 'white' : colors.text,
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontWeight: 600 }}>{action.label}</div>
                {action.sublabel && (
                  <div style={{
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    opacity: 0.8
                  }}>
                    {action.sublabel}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Close button (only if not loading and has onClose) */}
        {!content.showSpinner && onClose && (
          <button
            onClick={onClose}
            style={{
              marginTop: '1rem',
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              color: colors.textSecondary,
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionModal;
